import { z } from "zod";

import { GOVERNED_CAPABILITY_SURFACES, type GovernedCapabilitySurface } from "./surfaces";

export type { AgentCapabilityKey } from "./identity";
/** Browser-safe completeness and budget vocabulary for governed capabilities. */
export { AGENT_CAPABILITY_KEYS, AgentCapabilityKeySchema } from "./identity";

export const CompletenessBudgetClassSchema = z.union([
  z.literal("singleton"),
  z.literal("standard_population"),
  z.literal("note_envelope"),
]);
export type CompletenessBudgetClass = z.infer<typeof CompletenessBudgetClassSchema>;

export const CompletenessDegradationSchema = z.union([z.literal("aggregate_only"), z.literal("partial_evidence"), z.literal("refused")]);
export type CompletenessDegradation = z.infer<typeof CompletenessDegradationSchema>;

export const SurfaceCompletenessBudgetSchema = z
  .object({
    queryMaxRows: z.number().int().positive().nullable(),
    sourceMaterializationMaxRows: z.number().int().positive().nullable(),
    artifactMaxRows: z.number().int().positive(),
    artifactMaxBytes: z.number().int().positive().nullable(),
    projectedFrameMaxBytes: z.number().int().positive().nullable(),
    modelMaxRows: z.number().int().positive().nullable(),
    modelMaxBytes: z.number().int().positive().nullable(),
  })
  .strict();

export type SurfaceCompletenessBudget = z.infer<typeof SurfaceCompletenessBudgetSchema>;

const surfaceBudgetSchemas: Record<GovernedCapabilitySurface, z.ZodOptional<typeof SurfaceCompletenessBudgetSchema>> = {
  public_api: SurfaceCompletenessBudgetSchema.optional(),
  assistant: SurfaceCompletenessBudgetSchema.optional(),
};

export const CompletenessPolicySchema = z
  .object({
    populationSemantics: z.string().trim().min(1).max(120),
    populationNoun: z.string().trim().min(1).max(48),
    budgetClass: CompletenessBudgetClassSchema,
    degradation: z.array(CompletenessDegradationSchema).min(1),
    disclosure: z.string().trim().min(1).max(240),
    surfaceBudgets: z
      .object(surfaceBudgetSchemas)
      .strict()
      .refine(
        budgets => GOVERNED_CAPABILITY_SURFACES.some(surface => budgets[surface] !== undefined),
        "At least one surface budget is required.",
      ),
  })
  .strict()
  .superRefine((policy, context) => {
    if (new Set(policy.degradation).size !== policy.degradation.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["degradation"],
        message: "Degradation outcomes must be unique.",
      });
    }
  });

export type CompletenessPolicy = z.infer<typeof CompletenessPolicySchema>;

export const defineCompletenessPolicy = (policy: CompletenessPolicy): CompletenessPolicy => CompletenessPolicySchema.parse(policy);

export const PUBLIC_API_POPULATION_BUDGET: SurfaceCompletenessBudget = SurfaceCompletenessBudgetSchema.parse({
  queryMaxRows: 500,
  sourceMaterializationMaxRows: null,
  artifactMaxRows: 500,
  artifactMaxBytes: null,
  projectedFrameMaxBytes: null,
  modelMaxRows: null,
  modelMaxBytes: null,
});

export const STANDARD_POPULATION_BUDGET: SurfaceCompletenessBudget = SurfaceCompletenessBudgetSchema.parse({
  queryMaxRows: 10_000,
  sourceMaterializationMaxRows: null,
  artifactMaxRows: 10_000,
  artifactMaxBytes: 3_000_000,
  projectedFrameMaxBytes: 3_500_000,
  modelMaxRows: 50,
  modelMaxBytes: 100_000,
});

export const SINGLETON_BUDGET: SurfaceCompletenessBudget = SurfaceCompletenessBudgetSchema.parse({
  queryMaxRows: 1,
  sourceMaterializationMaxRows: null,
  artifactMaxRows: 1,
  artifactMaxBytes: 3_000_000,
  projectedFrameMaxBytes: 3_500_000,
  modelMaxRows: 1,
  modelMaxBytes: 100_000,
});

export const NOTE_DETAIL_BUDGET: SurfaceCompletenessBudget = SurfaceCompletenessBudgetSchema.parse({
  queryMaxRows: 10,
  sourceMaterializationMaxRows: null,
  artifactMaxRows: 10,
  artifactMaxBytes: 3_000_000,
  projectedFrameMaxBytes: 3_500_000,
  modelMaxRows: 10,
  modelMaxBytes: 100_000,
});

export const SALES_COMPARISON_SOURCE_SENTINEL_ROWS = 50_001;
export const TERMINAL_FRAME_RESERVE_BYTES = 500_000;

/**
 * A null budget bound means "no cap". Coalescing null to zero would invert
 * that into reject-everything; enforcement sites must coalesce to unlimited.
 */
export const budgetCapOrUnlimited = (bound: number | null | undefined): number => bound ?? Number.POSITIVE_INFINITY;
